import { RestApiClient } from 'ifield-core-components'
import _ from 'lodash'

/**
 * ラベルスタイルの変更判定
 * @param {Array.<Object>} oldLayers 旧レイヤー
 * @param {Array.<Object>} newLayers 新レイヤー
 * @yields {Object} ラベルのスタイルに変更がある新レイヤー
 */
const layersLabelStyleChanged = function* (oldLayers, newLayers) {
  for (const oldLayer of oldLayers) {
    const newLayer = newLayers.find((layer) => layer.id === oldLayer.id)
    if (!newLayer) continue

    const labelStyles = Object.entries(oldLayer.style).filter(([key]) =>
      /^label_/.test(key)
    )
    for (const [key, value] of labelStyles) {
      if (newLayer.style[key] !== value) {
        yield newLayer
        break
      }
    }
  }
}

/**
 * 初期スロットル間隔(ms)
 */
const INITIAL_THROTTLE_TIME = 30000

export default {
  namespaced: true,
  state: {
    layers: [],
    markerImageStore: new Map(),
    labelImageStores: [],
    throttleTime: INITIAL_THROTTLE_TIME,
    throttler: null,
    selectedFeature: null, // 選択された Feature (リアクティブ用)
    clickedFeature: null, // クリックされた Feature (イベント向け、２回クリックでも通知)
    enableVisibleAutoSave: true,
  },
  persistentStates: ['throttleTime'],
  mutations: {
    /**
     * stateを初期化する
     * @param {*} state
     */
    resetState(state) {
      state.layers = []
      state.markerImageStore = new Map()
      state.labelImageStores = []
      state.throttleTime = INITIAL_THROTTLE_TIME
      state.throttler = null
      state.selectedFeature = null
      state.clickedFeature = null
      state.enableVisibleAutoSave = true
    },
    /**
     * マップレイヤーを設定する
     * @param {*} state
     * @param {Array.<Object>} layers マップレイヤー
     */
    setMapLayers(state, layers) {
      state.layers = layers
    },
    /**
     * ラベル画像キャッシュを保存する
     * @param {*} state
     * @param {Array.<Object>} labelImageStores ラベルイメージ
     */
    saveLabelImageStores(state, labelImageStores) {
      state.labelImageStores = labelImageStores
    },
    /**
     * ラベル画像キャッシュアイテムを追加する
     * @param {*} state
     * @param {Object} item ラベル画像キャッシュアイテム
     */
    pushLabelImageStore(state, item) {
      state.labelImageStores.push(item)
    },
    /**
     * 指定IDのラベル画像キャッシュをクリアする
     * @param {*} state
     * @param {Number} id レイヤーID
     */
    clearLabelImageStore(state, id) {
      const storeItem = state.labelImageStores.find((item) => item.id === id)
      storeItem && storeItem.store.clear()
    },
    /**
     * スロットル間隔を保存する
     * @param {*} state
     * @param {Number} time スロットル間隔(ms)
     */
    setThrottleTime(state, time) {
      state.throttleTime = +time
    },
    /**
     * スロットル関数を保存する
     * @param {*} state
     * @param {Function} throttler スロットル関数
     */
    setThrottler(state, throttler) {
      state.throttler = throttler
    },
    /**
     * クリックされた Feature のデータ
     * @param {*} state
     * @param {Function} clickedFeature Feature データ
     */
    setClickedFeature(state, clickedFeature) {
      state.clickedFeature = clickedFeature
    },
    /**
     * 選択された Feature のデータ
     * @param {*} state
     * @param {Function} selectedFeature Feature データ
     */
    setSelectedFeature(state, selectedFeature) {
      state.selectedFeature = selectedFeature
    },
    /**
     * Indoor Level Renderer
     * @param {*} state
     * @param {Function} indoorLevelRenderer
     */
    setIndoorLevelRenderer(state, indoorLevelRenderer) {
      state.indoorLevelRenderer = indoorLevelRenderer
    },
    /**
     * 表示状態の自動保存状態
     * @param {*} state
     * @param {Boolean} value 自動保存時はtrue
     */
    setEnableVisibleAutoSave(state, value) {
      state.enableVisibleAutoSave = value
    },
  },
  getters: {
    /**
     * マップレイヤー
     * @param {*} state
     * @returns {Array.<Object>}
     */
    mapLayers: (state) => state.layers,
    /**
     * マーカーイメージ
     * @param {*} state
     * @returns {Map}
     */
    markerImageStore: (state) => state.markerImageStore,
    /**
     * ラベルイメージ
     * @param {*} state
     * @returns {Array.<Object>}
     */
    labelImageStores: (state) => state.labelImageStores,
    /**
     * クリック Feature
     * @param {*} state
     * @returns {Object} Feature データ
     */
    clickedFeature: (state) => state.clickedFeature,
    /**
     * クリックされた Feature Place
     * ※ clickedFeature に dispatcher 機能をもたせる
     * @param {*} state
     * @returns {Object} Feature データ
     */
    clickedPlace: (state) => {
      if (state.clickedFeature && state.clickedFeature.properties.place_id) {
        return state.clickedFeature
      }
      return null
    },
    /**
     * クリックされた Feature Indoor Building
     * ※ clickedFeature に dispatcher 機能をもたせる
     * @param {*} state
     * @returns {Object} Feature データ
     */
    clickedIndoorBuilding: (state) => {
      if (
        state.clickedFeature &&
        state.clickedFeature.properties.indoor_building_id
      ) {
        return state.clickedFeature
      }
      return null
    },
    /**
     * 選択された Feature
     * @param {*} state
     * @returns {Object} Feature データ
     */
    selectedFeature: (state) => state.selectedFeature,
    /**
     * 選択された Feature Place
     * ※ selectedFeature に dispatcher 機能をもたせる
     * @param {*} state
     * @returns {Object} Feature データ
     */
    selectedPlace: (state) => {
      if (state.selectedFeature && state.selectedFeature.properties.place_id) {
        return state.selectedFeature
      }
      return null
    },
    /**
     * 選択された Feature Indoor Building
     * ※ selectedFeature に dispatcher 機能をもたせる
     * @param {*} state
     * @returns {Object} Feature データ
     */
    selectedIndoorBuilding: (state) => {
      if (
        state.selectedFeature &&
        state.selectedFeature.properties.indoor_building_id
      ) {
        return state.selectedFeature
      }
      return null
    },
  },
  actions: {
    /**
     * stateをリセットする
     */
    resetState({ commit }) {
      commit('resetState')
    },
    /**
     * MapLayerを更新する
     */
    async updateMapLayers({ commit, dispatch }) {
      const url = RestApiClient.createApiUrl('map_layer')
      const json = await RestApiClient.executeGetApi(url)
      dispatch('pushLabelImageStoreIfNotExists', json.data)
      dispatch('clearLabelImageStoreByStyleChanged', json.data)
      commit('setMapLayers', json.data)
    },
    /**
     * スロットル間隔更新
     * @param {Number} time スロットル間隔(ms)
     */
    updateThrottleTime({ commit }, time) {
      commit('setThrottleTime', +time)
      commit('setThrottler', null)
    },
    /**
     * MapLayerを更新する(スロットル)
     */
    async updateMapLayersWithThrottle({ state, commit, dispatch }) {
      if (!state.throttler) {
        const throttler = _.throttle(
          () => dispatch('updateMapLayers'),
          state.throttleTime,
          { trailing: false }
        )
        commit('setThrottler', throttler)
      }
      return state.throttler()
    },
    /**
     * ラベル画像ストアの作成
     * @param {Array.<Object>} layers 更新予定のマップレイヤー
     */
    pushLabelImageStoreIfNotExists({ state, commit }, layers) {
      for (const layer of layers) {
        const targetIndex = state.labelImageStores.findIndex(
          (item) => item.id === layer.id
        )
        if (targetIndex === -1) {
          commit('pushLabelImageStore', {
            id: layer.id,
            store: new Map(),
          })
        }
      }
    },
    /**
     * ラベルスタイルが変更していたら画像ストアをクリアする
     * @param {Array.<Object>} layers 更新予定のマップレイヤー
     */
    clearLabelImageStoreByStyleChanged({ state, commit }, layers) {
      for (const layer of layersLabelStyleChanged(state.layers, layers)) {
        commit('clearLabelImageStore', layer.id)
      }
    },
    /**
     * feature がクリックされた
     * @param {Object} feature クリックされた Feature
     */
    onClickFeature({ commit }, feature) {
      // クリックイベント (２回クリックでもイベントを発行出来るように clone する)
      commit('setClickedFeature', { ...feature })

      // 選択されたオブジェクト
      commit('setSelectedFeature', feature)
    },
  },
}
